import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useLazyGetSubAdminQuery,
  useUpdateSubAdminMutation,
  useDeleteSubAdminMutation,
} from "../../service/subAdmin";
import { showError, showToast } from "../../constants/toasts";
import moment from "moment";
import Pagination from "../../components/Panigation";
import ConfirmationDialog from "../../Modals/confirmationModal";

const SubadminList = ({ search }: { search: string }) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [selected, setSelected] = useState<any | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const [getList, { isLoading }] = useLazyGetSubAdminQuery();
  const [updateSubAdmin, { isLoading: isUpdating }] =
    useUpdateSubAdminMutation();
  const [deleteSubAdmin, { isLoading: isDeleting }] =
    useDeleteSubAdminMutation();

  const columns = [
    { id: "name", label: "Name" },
    { id: "phone", label: "Phone Number" },
    { id: "role", label: "Role" },
    { id: "date", label: "Created Date" },
    { id: "status", label: "Status" },
    { id: "action", label: "Action" },
  ];

  const fetchList = async (p?: number) => {
    try {
      const res = await getList({
        page: p || page,
        limit,
        ...(search && { search }),
      }).unwrap();
      if (res?.code === 200) {
        setRows(res?.data?.list || []);
        setTotalCount(res?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setRows([]);
      setTotalCount(0);
    }
  };

  const handleStatusChange = async (row: any) => {
    try {
      const response = await updateSubAdmin({
        id: row?._id,
        body: { isBlocked: !row?.isBlocked },
      }).unwrap();
      if (response?.code === 200) {
        showToast(
          `Sub-admin ${!row?.isBlocked ? "blocked" : "activated"} successfully`
        );
        setRows((prev) =>
          prev.map((r) =>
            r._id === row._id ? { ...r, isBlocked: !row.isBlocked } : r
          )
        );
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update sub-admin status");
    }
  };

  const openDelete = (row: any) => {
    setSelected(row);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setSelected(null);
    setDeleteOpen(false);
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteSubAdmin({ id: selected?._id }).unwrap();
      if (res?.code === 200) {
        showToast("Sub-admin deleted successfully");
        closeDelete();
        if (rows.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
          fetchList(page - 1);
        } else {
          fetchList(page);
        }
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to delete sub-admin");
    }
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    fetchList(p);
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {columns?.map((column) => (
                <TableCell key={column?.id}>{column?.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.length > 0 ? (
              rows?.map((row) => (
                <TableRow hover key={row?._id}>
                  <TableCell>
                    <a
                      href={`/manage-subadmin/detail/${row?._id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/manage-subadmin/detail/${row?._id}`);
                      }}
                      className="user_block click_action"
                    >
                      <figure>
                        <img
                          src={
                            row?.image || "/static/images/user_placeholder.png"
                          }
                          alt="User"
                        />
                      </figure>
                      <p>
                        <b>
                          {`${row?.firstName || ""} ${
                            row?.lastName || ""
                          }`.trim()}
                        </b>
                        <span>{row?.email || "-"}</span>
                        <span>{`${row?.countryCode || ""}${
                          row?.phone || ""
                        }`}</span>
                      </p>
                    </a>
                  </TableCell>
                  <TableCell>{`${row?.countryCode || ""}${
                    row?.phone || ""
                  }`}</TableCell>
                  <TableCell>{row?.accessRole?.name || "-"}</TableCell>
                  <TableCell>
                    {row?.createdAt
                      ? moment(row?.createdAt).format(" lll ")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <SwitchToggle
                      onChange={() => handleStatusChange(row)}
                      checked={!row?.isBlocked}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <Box>
                        <IconButton
                          onClick={() =>
                            navigate(`/manage-subadmin/detail/${row?._id}`)
                          }
                        >
                          <img src="/static/images/eye_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton
                          onClick={() =>
                            navigate(`/manage-subadmin/edit/${row?._id}`)
                          }
                        >
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton onClick={() => openDelete(row)}>
                          <img src="/static/images/trash2_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow hover>
                <TableCell colSpan={6}>
                  <Box textAlign={"center"}>No data found</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount > 0 && (
        <Pagination
          module={rows}
          page={page}
          onPageChange={handlePageChange}
          totalPages={totalPages}
          setPage={setPage}
          totalCount={totalCount}
        />
      )}

      <ConfirmationDialog
        open={deleteOpen}
        onClose={closeDelete}
        onConfirm={handleDelete}
        title="Delete Sub-Admin"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Are you sure you want to delete sub-admin "${
          (selected?.firstName || "") + " " + (selected?.lastName || "")
        }"?`}
        isLoading={isDeleting}
      />
    </>
  );
};

export default SubadminList;
